import * as Sentry from "@sentry/browser";
import { ApolloError } from "apollo-boost";
import * as React from "react";
import { SeverityLevel } from "@sentry/types";

type ErrorBoundaryState = {
  hasError: boolean;
  error?: ApolloError;
  eventId?: string;
};
type ErrorBoundaryProps = {
  render: () => React.ReactNode;
  renderErrorPage: (error: ApolloError) => React.ReactNode;
};

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.warn("[Error Boundary]", error, errorInfo);
    if (!/ResizeObserver loop limit/.test(error?.message || "")) {
      const level: SeverityLevel =
        error && error.message.startsWith("GraphQL error: 4")
          ? "warning"
          : "fatal";
      Sentry.withScope((scope) => {
        // @ts-ignore
        scope.setExtras(errorInfo);
        scope.setTag("source", "error-boundary");
        scope.setLevel(level);
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    }
  }

  render() {
    return this.state.hasError
      ? this.props.renderErrorPage(this.state.error!)
      : this.props.render();
  }
}
